import { SupabaseClient } from "@supabase/supabase-js";
import { EventVenueCandidate } from "../venues";
import { getAllAdminEmails } from "./database_admin";
import { getEvent } from "./database";
import { getCurrentUser } from "@/lib/db/users";
import { sendSlackMessage } from "@/components/ui/utils";
import { SlackNotificationType } from "@/lib/shared/enums";

/**
 * Tracks a venue candidate view in the database.
 *
 * @param supabase - The SupabaseClient instance.
 * @param candidate - The event venue candidate to track.
 * @param userId - The ID of the user who viewed the candidate.
 */
export async function trackVenueCandidateView(
  supabase: SupabaseClient,
  candidate: EventVenueCandidate,
  userId: number
): Promise<void> {
  // First try to get existing record
  const { data: existing } = await supabase
    .schema("nowadays")
    .from("candidate_viewers")
    .select("count")
    .eq("event_venue_candidate_id", candidate.event_venue_candidate_id)
    .eq("user_id", userId)
    .single();

  const { error } = await supabase
    .schema("nowadays")
    .from("candidate_viewers")
    .upsert(
      {
        event_venue_candidate_id: candidate.event_venue_candidate_id,
        user_id: userId,
        count: existing ? existing.count + 1 : 1,
      },
      {
        onConflict: "event_venue_candidate_id,user_id",
      }
    );

  if (error) {
    console.error("[trackVenueCandidateView]", error);
  }
}

/**
 * Checks if a user has viewed a venue candidate.
 *
 * @param supabase - The SupabaseClient instance.
 * @param candidateId - The event venue candidate ID to check.
 * @param userId - The ID of the user to check.
 * @returns True if the user has viewed the candidate, false otherwise.
 */
export async function hasUserViewedCandidate(
  supabase: SupabaseClient,
  candidateId: number,
  userId: number
): Promise<boolean> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("candidate_viewers")
    .select("id")
    .eq("event_venue_candidate_id", candidateId)
    .eq("user_id", userId)
    .maybeSingle();

  if (error) {
    console.error("[hasUserViewedCandidate]", error);
    return false;
  }

  return data !== null;
}

/**
 * Checks if any non-admin users have viewed a venue candidate for a specific event.
 * This is used by admins to see if the venue is "new" to the event's non-admin users.
 *
 * @param supabase - The SupabaseClient instance.
 * @param candidateId - The event venue candidate ID to check.
 * @param eventId - The ID of the event to check users for.
 * @returns True if any non-admin user has viewed the candidate, false otherwise.
 */
export async function hasAnyNonAdminViewedCandidate(
  supabase: SupabaseClient,
  candidateId: number,
  eventId: number
): Promise<boolean> {
  // Get all users with access to this event (excluding admins)
  const { data: eventUsers, error: eventUsersError } = await supabase
    .schema("nowadays")
    .from("user_event_access")
    .select(
      `
      user_id,
      users:users!user_permissions_user_id_fkey(
        email
      )
    `
    )
    .eq("event_id", eventId);

  if (eventUsersError || !eventUsers) {
    console.error("[hasAnyNonAdminViewedCandidate] Error fetching event users:", eventUsersError);
    return false;
  }

  // Get all admin emails
  const adminEmailsList = await getAllAdminEmails(supabase);
  const adminEmails = adminEmailsList.map((email) => ({ email }));

  if (!adminEmails || adminEmails.length === 0) {
    console.error("[hasAnyNonAdminViewedCandidate] Error fetching admin emails");
    return false;
  }

  const adminEmailSet = new Set(adminEmails.map((admin) => admin.email));

  // Filter to get non-admin user IDs
  const nonAdminUserIds = eventUsers
    .filter((eventUser) => {
      const userEmail = (eventUser.users as any)?.email;
      return userEmail && !adminEmailSet.has(userEmail);
    })
    .map((eventUser) => eventUser.user_id);

  if (nonAdminUserIds.length === 0) {
    return false; // No non-admin users for this event
  }

  // Check if any non-admin user has viewed this candidate
  const { data, error } = await supabase
    .schema("nowadays")
    .from("candidate_viewers")
    .select("id")
    .eq("event_venue_candidate_id", candidateId)
    .in("user_id", nonAdminUserIds)
    .limit(1);

  if (error) {
    console.error("[hasAnyNonAdminViewedCandidate] Error checking views:", error);
    return false;
  }

  return data && data.length > 0;
}

/**
 * Checks if a venue is already added to an event in the 'event_venue_candidate' table in Supabase.
 *
 * @param supabase - The SupabaseClient instance.
 * @param eventId - The ID of the event to check.
 * @returns An array of venue_static_id that are already added to the event.
 */
export async function venuesAlreadyAddedToEventVenueCandidate(
  supabase: SupabaseClient,
  eventId: number
): Promise<number[]> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("event_venue_candidate")
    .select("venue_static_id")
    .eq("event_id", eventId);

  if (error) {
    console.error("[venuesAlreadyAddedToEventVenueCandidate]", error);
    return [];
  }

  return data.map((item) => item.venue_static_id);
}

/**
 * Fetches an event venue candidate from the database.
 *
 * @param supabase - The SupabaseClient instance.
 * @param eventId - The ID of the event to fetch the venue candidate for.
 * @param venueStaticId - The ID of the venue to fetch the candidate for.
 * @returns The event venue candidate.
 */
export async function getEventVenueCandidate(
  supabase: SupabaseClient,
  eventId: number,
  venueStaticId: number
): Promise<EventVenueCandidate | null> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("event_venue_candidate")
    .select("*")
    .eq("event_id", eventId)
    .eq("venue_static_id", venueStaticId)
    .maybeSingle();

  if (error) {
    console.error("[getEventVenueCandidate]", error);
    return null;
  }

  return data;
}

/**
 * Fetches multiple event venue candidates in batch.
 *
 * @param supabase - The SupabaseClient instance.
 * @param eventVenuePairs - Array of {eventId, venueStaticId} pairs to fetch.
 * @returns Map of "eventId-venueStaticId" to EventVenueCandidate.
 */
export async function getEventVenueCandidatesBatch(
  supabase: SupabaseClient,
  eventVenuePairs: Array<{ eventId: number; venueStaticId: number }>
): Promise<Map<string, EventVenueCandidate>> {
  if (eventVenuePairs.length === 0) {
    return new Map();
  }

  // Build an OR filter for all pairs
  const orFilters = eventVenuePairs
    .map((pair) => `and(event_id.eq.${pair.eventId},venue_static_id.eq.${pair.venueStaticId})`)
    .join(",");

  const { data, error } = await supabase.schema("nowadays").from("event_venue_candidate").select("*").or(orFilters);

  if (error) {
    console.error("[getEventVenueCandidatesBatch]", error);
    return new Map();
  }

  const resultMap = new Map<string, EventVenueCandidate>();
  for (const candidate of data || []) {
    const key = `${candidate.event_id}-${candidate.venue_static_id}`;
    resultMap.set(key, candidate);
  }

  return resultMap;
}

/**
 * Adds a venue to an event as an event venue candidate.
 * If the venue is already added, it will update the existing record with new information.
 *
 * @param supabase - The SupabaseClient instance.
 * @param eventId - The ID of the event to add the venue to.
 * @param venueStaticId - The ID of the venue to add.
 * @param minutesFromOrigin - Optional distance from origin in minutes.
 * @param modeOfTravelFromOrigin - Optional mode of travel from origin.
 * @param customOriginName - Optional custom origin name.
 * @param showOnDash - Whether to show the venue on the dashboard (default: true).
 * @param source - Optional source of the venue.
 * @param ranking - Optional ranking of the venue.
 * @param markedAsLiked - Optional flag for whether the venue is marked as liked.
 * @param markedAsLikedUserId - Optional user ID who marked the venue as liked.
 * @param customerAdded - Optional flag for whether the venue was added by a customer.
 * @returns The event venue candidate record, or null if the event is not found.
 */
export async function addVenueToEventVenueCandidate(
  supabase: SupabaseClient,
  eventId: number,
  venueStaticId: number,
  minutesFromOrigin?: number | null,
  modeOfTravelFromOrigin?: string | null,
  customOriginName?: string | null,
  showOnDash: boolean = true,
  source?: string,
  ranking?: number,
  markedAsLiked?: boolean,
  markedAsLikedUserId?: number,
  customerAdded?: boolean
): Promise<EventVenueCandidate | null> {
  const event = await getEvent(supabase, eventId);
  if (!event) {
    console.log(`Event with id ${eventId} not found.`);
    return null;
  }

  const user = await getCurrentUser(supabase);
  const added_by_user_id = user?.user_id;

  console.log(event);
  const orgId = event.org_id;

  const existingVenueStaticIds = await venuesAlreadyAddedToEventVenueCandidate(supabase, eventId);

  let candidateToReturn: EventVenueCandidate | null = null;

  if (existingVenueStaticIds.includes(venueStaticId)) {
    console.log(`Venue ${venueStaticId} is already added to event ${eventId}.`);

    const existingVenue = await getEventVenueCandidate(supabase, eventId, venueStaticId);
    if (existingVenue) {
      const updateFields: Record<string, any> = {};
      if (minutesFromOrigin && existingVenue.distance_from_origin_in_minutes !== minutesFromOrigin) {
        updateFields.distance_from_origin_in_minutes = minutesFromOrigin;
      }
      if (modeOfTravelFromOrigin && existingVenue.mode_of_travel_from_origin !== modeOfTravelFromOrigin) {
        updateFields.mode_of_travel_from_origin = modeOfTravelFromOrigin;
      }
      if (customOriginName && existingVenue.custom_origin_name !== customOriginName) {
        updateFields.custom_origin_name = customOriginName;
      }

      if (Object.keys(updateFields).length > 0) {
        const { error } = await supabase
          .schema("nowadays")
          .from("event_venue_candidate")
          .update(updateFields)
          .eq("event_id", eventId)
          .eq("venue_static_id", venueStaticId);

        if (error) {
          console.error("Error updating venue:", error);
        } else {
          console.log(`Updated venue ${venueStaticId} with new information.`);
        }
      }

      candidateToReturn = existingVenue;
    }
  } else {
    const data: Record<string, any> = {
      event_id: eventId,
      venue_static_id: venueStaticId,
      show_on_dashboard: showOnDash,
      source: source,
      added_by_user_id: added_by_user_id,
    };

    if (minutesFromOrigin !== undefined) {
      data.distance_from_origin_in_minutes = minutesFromOrigin;
    }
    if (modeOfTravelFromOrigin !== undefined) {
      data.mode_of_travel_from_origin = modeOfTravelFromOrigin;
    }
    if (customOriginName !== undefined) {
      data.custom_origin_name = customOriginName;
    }
    if (ranking !== undefined) {
      data.ranking = ranking;
    }
    if (markedAsLiked !== undefined) {
      data.marked_as_liked = markedAsLiked;
    }
    if (markedAsLikedUserId !== undefined) {
      data.marked_as_liked_user_id = markedAsLikedUserId;
    }
    if (customerAdded !== undefined) {
      data.customer_added = customerAdded;
    }

    const { data: newCandidate, error } = await supabase
      .schema("nowadays")
      .from("event_venue_candidate")
      .insert(data)
      .select("*")
      .single();

    if (error) {
      console.error("Error adding venue to event:", error);
    } else {
      await sendSlackMessage(SlackNotificationType.EMAIL, "New Venue Added to Event", undefined, [
        { title: "Event ID", message: eventId.toString() },
        { title: "Venue Static ID", message: venueStaticId.toString() },
        { title: "Shown on Dashboard", message: showOnDash.toString() },
      ]);
      console.log(`Successfully added venue ${venueStaticId} to event ${eventId} for org ${orgId}`);
      candidateToReturn = newCandidate;
    }
  }

  // Return the candidate record
  return candidateToReturn;
}
